/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState } from 'react';
import {
  Actions,
  Async,
  Dispatchers,
  F1,
  toAsyncFetched,
  TypedOmit,
} from '@client/new/helpers/types';
import { getDispatchers, toMap, useMap } from '@client/new/helpers/types-utils';
import {
  Workout,
  Tabata,
  HIIT,
  ExerciseSettings,
  ParentExercise,
} from './workouts.domain';
import { hiitSchema, tabataSchema } from './workout.schema';
import {
  getErrors,
  toHIIT,
  toParentExercises,
  toTabata,
} from './workout.mappers';
import { legacyEditWorkoutVM } from '../../workout/WorkoutEdit';
import {
  DelegatedActionMap,
  LegacyWorkoutFormValues,
} from '../../workout/models/workoutEdit.model';
import { createUseVM, FormVM } from './form.utils';
import { combineWorkoutWithLegacyData } from './workout.legacy-bindings';
import {
  addAutoplayed,
  addSubExercise,
  addSubExercises,
  toWorkout,
  removeSubExercise,
  reorder,
  VMCallback,
  applySettings,
  getEmptyWorkout,
  applySetsCount,
  getParentId,
  removeExercise,
} from './workout.viewModel.state';
import { SubExerciseDTO } from '../model/workout.domain';
import { formatTime } from '@design-system/components/time-input';
import { useFormMap } from '@client/new/helpers/form/useFormMap';
import { useWorkoutEditorExerciseSuggestionsModel } from '../model/exerciseSuggestions.model';
import { ExerciseSuggestion } from '@client/new/pages/creator/library/models/exercise-suggestions.model';

export type WorkoutDispatch = Actions<WorkoutVMActionMap>;
export type WorkoutVMAction = Actions<WorkoutVMActionMap>;

export type WorkoutVM = Workout &
  FormVM &
  Dispatchers<WorkoutVMActionMap> & {
    getSettings: (id: string) => ExerciseSettings;
    getExerciseSuggestions: (subExerciseId: string) => ExerciseSuggestion[];
    exerciseSuggestions: Async<ExerciseSuggestion[]>;
  };

export type WorkoutVMActionMap = DelegatedActionMap & {
  openLibrary: {
    parentId: string;
    action: 'add' | 'update';
    exerciseId?: string;
  };
  addSubExercise: { parentId: string };
  updateAutoplayedSettings: ExerciseSettings;
  reorder: Workout['exerciseIds'];
  updateTitle: { id: string; title: string };
  removeSubExercise: { exerciseId: string };
  setExerciseSearch: { subExerciseId: string; query: string };
  clearExerciseSearch: null;
};

export const useWorkoutVM = (): Async<WorkoutVM> => {
  const legacyEditWorkout = legacyEditWorkoutVM.useValue();
  const asyncModel = legacyEditWorkout.model.refinedWorkoutModel;
  const [vm, setVM] = useState(getEmptyWorkout());
  const [exerciseSearch, setExerciseSearch] = useState<{
    subExerciseId: string | null;
    query: string;
  }>({ subExerciseId: null, query: '' });
  const exerciseSuggestions = useWorkoutEditorExerciseSuggestionsModel(
    exerciseSearch.query,
  );
  const { formMap, ...form } = useFormMap();
  const [mediaCbs, setMediaCbs] =
    useMap<F1<WorkoutVMActionMap['updateSubExerciseMedia']>>();

  const [, setTitlesMap] = useMap<string>();

  useEffect(() => {
    legacyEditWorkout.model.setDispatchCb(() => dispatch);
    legacyEditWorkout.setDispatchCb(dispatch);
  }, [vm, formMap]);

  useEffect(() => {
    if (asyncModel.type !== 'fetched') return;
    const vm = toWorkout(asyncModel.value.exercises);
    const titles = toMap(asyncModel.value.exercises, 'id', (v) => v.name);
    setTitlesMap(titles);
    legacyEditWorkout.dispatch('updateAutoplayedTitlesMap', titles);
    legacyEditWorkout.dispatch(
      'updateAutoplayedSettings',
      vm.exerciseSettingsMap,
    );
    setVM(vm);
  }, [asyncModel.type]);

  const getExercise = useCallback(
    (id: string) => vm.exerciseMap[id].value as any,
    [vm],
  );

  const getSettings = useCallback(
    (id: string) => vm.exerciseSettingsMap[getParentId(vm, id)],
    [vm],
  );

  const getDuration = useCallback(
    (id: string) => {
      const parentEl = vm.exerciseIds.find((e) => e.id === id);
      const parent = vm.exerciseMap[id]['value'] as ParentExercise['value'];
      return formatTime(
        parentEl?.items
          .map(
            (i) =>
              (vm.exerciseMap[i.id]['value'] as SubExerciseDTO)?.sets || [],
          )
          .reduce(
            (acc, ss) => ss.reduce((acc, s) => acc + s.time + s.rest, acc),
            (parent?.longRest || 0) * (parent?.rounds || 0),
          ) || 0,
      );
    },
    [vm],
  );

  const getExerciseSuggestions = useCallback(
    (_subExerciseId: string) => {
      if (exerciseSuggestions.type !== 'fetched') return [];
      return exerciseSuggestions.value;
    },
    [exerciseSuggestions],
  );

  const dispatchWithState = useCallback(
    (a: WorkoutVMAction): VMCallback | null => {
      switch (a.type) {
        case 'addAutoplayed':
          return addAutoplayed(a.payload, legacyEditWorkout.dispatch);
        case 'addSubExercise':
          return addSubExercise(a.payload.parentId);
        case 'addSubExercises':
          return addSubExercises(a.payload);
        case 'removeSubExercise':
          return removeSubExercise(a.payload.exerciseId, form);
        case 'reorder':
          return reorder(a.payload);
        case 'updateAutoplayedSettings':
          return applySettings(a.payload, formMap);
        case 'removeExercise':
          return removeExercise(a.payload.exerciseId, form);
      }
      return null;
    },
    [formMap],
  );

  const [showErrors, setShowErrors] = useState(false);

  const triggerValidation = () => {
    const validation = Object.values(formMap).map((f) => f.trigger());
    return Promise.all(validation);
  };

  const extendLegacyWorkout = (w: LegacyWorkoutFormValues) =>
    combineWorkoutWithLegacyData(w, toParentExercises({ ...vm, formMap }));

  const dispatch = (a: WorkoutVMAction) => {
    const vmCallback = dispatchWithState(a);
    if (vmCallback) return setVM(vmCallback);

    switch (a.type) {
      case 'setExerciseSearch': {
        const nextQuery = a.payload.query.length >= 2 ? a.payload.query : '';
        setExerciseSearch({
          subExerciseId: a.payload.subExerciseId,
          query: nextQuery,
        });
        return;
      }
      case 'clearExerciseSearch':
        setExerciseSearch({ subExerciseId: null, query: '' });
        return;
      case 'publish':
        triggerValidation().then(() => {
          const errors = getErrors(formMap);
          if (Object.keys(errors).length > 0) {
            legacyEditWorkout.model.dispatch('setErrors', errors);
            setShowErrors(true);
            return;
          }
          setShowErrors(false);
          legacyEditWorkout.model.dispatch('setErrors', {});

          return legacyEditWorkout.model.dispatch(
            'extendedPublish',
            extendLegacyWorkout(a.payload),
          );
        });
        break;

      case 'save':
        triggerValidation().then(() => {
          const errors = getErrors(formMap);
          if (Object.keys(errors).length > 0) {
            legacyEditWorkout.model.dispatch('setErrors', errors);
            setShowErrors(true);
            return;
          }

          setShowErrors(false);
          legacyEditWorkout.model.dispatch('setErrors', {});

          return legacyEditWorkout.model.dispatch(
            'extendedSave',
            extendLegacyWorkout(a.payload),
          );
        });

        return;

      case 'schedule':
        triggerValidation().then(() => {
          const errors = getErrors(formMap);
          if (Object.keys(errors).length > 0) {
            legacyEditWorkout.model.dispatch('setErrors', errors);
            setShowErrors(true);
            return;
          }

          setShowErrors(false);
          legacyEditWorkout.model.dispatch('setErrors', {});

          return legacyEditWorkout.model.dispatch(
            'extendedSave',
            extendLegacyWorkout(a.payload),
          );
        });
        break;

      case 'openLibrary':
        return legacyEditWorkout.dispatch('openLibrary', {
          ...a.payload,
          type: 'short',
        });

      case 'updateSubExerciseMedia':
        return updateTabataExerciseMedia(a.payload);
    }
  };

  const dispatchers = getDispatchers<WorkoutVMActionMap>(dispatch);

  const updateTile = (p?: { title: string; id: string }) =>
    setTitlesMap((prev) => {
      const titles = { ...prev, ...(p ? { [p.id]: p.title } : {}) };
      setTitlesMap(titles);
      legacyEditWorkout.dispatch('updateAutoplayedTitlesMap', titles);
      return titles;
    });

  const updateTabataExerciseMedia = (
    p: WorkoutVMActionMap['updateSubExerciseMedia'],
  ) => mediaCbs[p.subExerciseId || '']?.(p);

  if (asyncModel.type !== 'fetched') return asyncModel;
  return toAsyncFetched({
    showErrors,
    ...dispatchers,
    ...vm,
    ...form,
    getSettings,
    getExercise,
    getExerciseSuggestions,
    exerciseSuggestions,
    setExercise: (id, e) => {
      setVM((vm) => {
        const exerciseMap = { ...vm.exerciseMap, [id]: e };

        if (
          e.type === 'HIIT' &&
          e.value.rounds !== (vm.exerciseMap[id] as HIIT).value.rounds
        )
          return applySetsCount(id, formMap)({ ...vm, exerciseMap });
        return { ...vm, exerciseMap };
      });
    },
    getDuration,
    subscribeOnMedia: (id, cb) =>
      setMediaCbs((mediaCbs) => ({ ...mediaCbs, [id]: cb })),
    formMap,
    updateTile,
  });
};

type TabataVM = TypedOmit<Tabata['value'], 'exercises'>;
export const useTabataVM = createUseVM<TabataVM, Tabata>(
  tabataSchema as any,
  (v) => toTabata({ ...v, exercises: [] }),
);

type HIITVM = TypedOmit<HIIT['value'], 'exercises'>;
export const useHIITVM = createUseVM<HIITVM, HIIT>(hiitSchema as any, (v) =>
  toHIIT({ ...v, goal: 'time', exercises: [] }),
);
